import { isRecord, readArray, readNumber, readString, type CartLine, type StorefrontCart, type StorefrontProduct } from "./types";

type EcommerceItem = { item_id: string; item_name: string; price?: number; quantity: number; item_variant?: string; item_brand?: string; discount?: number; index?: number; item_list_id?: string; item_list_name?: string };
type Ecommerce = { currency: string; value?: number; items: EcommerceItem[]; transaction_id?: string; tax?: number; shipping?: number; coupon?: string; item_list_id?: string; item_list_name?: string };

declare global {
  interface Window {
    dataLayer?: unknown[];
    __sculptoWebshopsDataLayer?: boolean;
  }
}

function money(minor: number | undefined, digits = 2): number | undefined {
  if (minor === undefined || !Number.isFinite(minor)) return undefined;
  const precision = Number.isInteger(digits) && digits >= 0 && digits <= 6 ? digits : 2;
  return Number((minor / 10 ** precision).toFixed(precision));
}

function item(line: CartLine, digits: number, quantity = line.quantity): EcommerceItem {
  const price = money(line.unitPrice, digits);
  return {
    item_id: line.productId ?? line.id,
    item_name: line.name,
    ...(price !== undefined ? { price } : {}),
    quantity,
    ...(line.variantId ? { item_variant: line.variantId } : {}),
  };
}

function cartEcommerce(cart: StorefrontCart): Ecommerce {
  const digits = cart.currencyMinorDigits ?? 2;
  const value = money(cart.subtotal === undefined ? undefined : cart.subtotal - (cart.discountTotal ?? 0), digits);
  return {
    currency: cart.currency,
    ...(value !== undefined ? { value } : {}),
    ...(cart.couponCode ? { coupon: cart.couponCode } : {}),
    items: cart.items.map((line) => item(line, digits)),
  };
}

export function pushEcommerce(event: string, ecommerce: Ecommerce): boolean {
  if (typeof window === "undefined" || !ecommerce.items.length) return false;
  // Sitekit intentionally does not install a Google tag before consent. Do not
  // create a queue that it could replay when consent is granted later.
  if (!window.dataLayer) {
    try {
      const consent = JSON.parse(window.localStorage.getItem("sculptocms:consent:v1") ?? "null");
      if (!isRecord(consent) || consent.analytics !== true) return false;
    } catch { return false; }
    window.dataLayer = [];
    window.__sculptoWebshopsDataLayer = true;
  }
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce });
  if (typeof CustomEvent !== "undefined") window.dispatchEvent?.(new CustomEvent("webshops:ga4-ecommerce"));
  return true;
}

export function trackProductView(product: StorefrontProduct): boolean {
  const price = product.salePrice ?? product.price;
  const value = money(price);
  return pushEcommerce("view_item", {
    currency: product.currency ?? "EUR",
    ...(value !== undefined ? { value } : {}),
    items: [{
      item_id: product.id,
      item_name: product.name,
      ...(value !== undefined ? { price: value } : {}),
      quantity: 1,
      ...(product.variantId ? { item_variant: product.variantId } : {}),
      ...(product.brands?.[0]?.name ? { item_brand: product.brands[0].name } : {}),
    }],
  });
}

function listItem(product: StorefrontProduct, index: number, listId: string, listName: string): EcommerceItem {
  const price = money(product.salePrice ?? product.price);
  return {
    item_id: product.id,
    item_name: product.name,
    quantity: 1,
    index,
    item_list_id: listId,
    item_list_name: listName,
    ...(price !== undefined ? { price } : {}),
    ...(product.brands?.[0]?.name ? { item_brand: product.brands[0].name } : {}),
  };
}

export function trackItemList(products: StorefrontProduct[], listId: string, listName: string): boolean {
  if (!products.length) return false;
  return pushEcommerce("view_item_list", {
    currency: products[0]?.currency ?? "EUR",
    item_list_id: listId,
    item_list_name: listName,
    items: products.map((product, index) => listItem(product, index, listId, listName)),
  });
}

export function trackItemSelection(product: StorefrontProduct, index: number, listId: string, listName: string): void {
  pushEcommerce("select_item", {
    currency: product.currency ?? "EUR",
    item_list_id: listId,
    item_list_name: listName,
    items: [listItem(product, index, listId, listName)],
  });
}

export function trackCartView(event: "view_cart" | "begin_checkout", cart: StorefrontCart): boolean {
  return pushEcommerce(event, cartEcommerce(cart));
}

export function trackCartMutation(method: "POST" | "PATCH" | "DELETE", body: Record<string, unknown>, previous: StorefrontCart | null, next: StorefrontCart): void {
  const digits = next.currencyMinorDigits ?? 2;
  const requestedId = typeof body.itemId === "string" ? body.itemId : undefined;
  if (method === "POST") {
    const line = next.items.find((entry) => entry.productId === body.productId && (!body.variantId || entry.variantId === body.variantId));
    const oldQuantity = previous?.items.find((entry) => entry.id === line?.id)?.quantity ?? 0;
    const quantity = (line?.quantity ?? 0) - oldQuantity;
    if (line && quantity > 0) {
      const value = money(line.unitPrice === undefined ? undefined : line.unitPrice * quantity, digits);
      pushEcommerce("add_to_cart", { currency: next.currency, ...(value !== undefined ? { value } : {}), items: [item(line, digits, quantity)] });
    }
    return;
  }
  if (!previous || !requestedId) return;
  const before = previous.items.find((entry) => entry.id === requestedId);
  if (!before) return;
  const after = next.items.find((entry) => entry.id === requestedId);
  const difference = (after?.quantity ?? 0) - before.quantity;
  if (!difference) return;
  const line = difference > 0 ? after! : before;
  const quantity = Math.abs(difference);
  const value = money(line.unitPrice === undefined ? undefined : line.unitPrice * quantity, digits);
  pushEcommerce(difference > 0 ? "add_to_cart" : "remove_from_cart", {
    currency: next.currency,
    ...(value !== undefined ? { value } : {}),
    items: [item(line, digits, quantity)],
  });
}

export function markPendingPurchase(orderId: string): void {
  if (typeof window === "undefined" || !orderId) return;
  try { window.sessionStorage.setItem(`webshops:ga4:pending:${orderId}`, "1"); } catch { /* Tracking storage can be unavailable. */ }
}

export function trackPurchase(value: unknown): void {
  if (typeof window === "undefined" || !isRecord(value)) return;
  const id = readString(value, "id");
  const status = readString(value, "status");
  const paymentStatus = readString(value, "paymentStatus");
  if (!id || ["cancelled", "canceled", "failed", "refunded"].includes(status ?? "") || ["failed", "cancelled", "canceled", "refunded"].includes(paymentStatus ?? "")) return;
  try { if (window.sessionStorage.getItem(`webshops:ga4:pending:${id}`) !== "1") return; } catch { return; }
  const rawItems = readArray(value, "items").filter(isRecord);
  const digits = readNumber(value, "currencyMinorDigits") ?? 2;
  const items = rawItems.flatMap((entry): EcommerceItem[] => {
    const itemId = readString(entry, "productId", "sku", "id");
    const name = readString(entry, "name");
    if (!itemId || !name) return [];
    const quantity = readNumber(entry, "quantity") ?? 1;
    const discountMinor = readNumber(entry, "discountMinor") ?? 0;
    const price = money(readNumber(entry, "unitPriceMinor"), digits);
    const variantId = readString(entry, "variantId");
    return [{ item_id: itemId, item_name: name, quantity,
      ...(price !== undefined ? { price } : {}),
      ...(discountMinor > 0 ? { discount: money(discountMinor / quantity, digits) } : {}),
      ...(variantId ? { item_variant: variantId } : {}),
    }];
  });
  if (!items.length) return;
  const key = `webshops:ga4:purchase:${id}`;
  try { if (window.localStorage.getItem(key)) return; } catch { /* Still emit without persistence. */ }
  const subtotal = readNumber(value, "subtotalMinor");
  const merchandiseValue = money(subtotal === undefined ? undefined : subtotal - (readNumber(value, "discountMinor") ?? 0), digits);
  const tax = money(readNumber(value, "taxMinor"), digits);
  const shipping = money(readNumber(value, "shippingMinor"), digits);
  const coupon = readString(value, "couponCode");
  const ecommerce: Ecommerce = {
    transaction_id: id,
    currency: readString(value, "currency") ?? "EUR",
    ...(merchandiseValue !== undefined ? { value: merchandiseValue } : {}),
    ...(tax !== undefined ? { tax } : {}),
    ...(shipping !== undefined ? { shipping } : {}),
    ...(coupon ? { coupon } : {}),
    items,
  };
  if (pushEcommerce("purchase", ecommerce)) {
    try { window.localStorage.setItem(key, "1"); } catch { /* Storage is optional. */ }
    try { window.sessionStorage.removeItem(`webshops:ga4:pending:${id}`); } catch { /* Storage is optional. */ }
  }
}
